/* eslint-disable react-hooks/exhaustive-deps */
import { debounce } from 'lodash';
import { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import DownloadingIcon from '@mui/icons-material/Downloading';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import { NavLink } from 'react-router';
import {
  InputAdornment,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TextField,
  TableBody,
} from '@mui/material';

import type { ChargerFlow } from '@models/chargerFlows';
import { Breakpoints } from '@utils/constants';
import { formatIsoToDate } from '@utils/date';

import { useMediaQuery } from '@hooks/useMediaQuery';
import { useAllChargerFlows } from '@features/chargerFlows/queries';
import { useSetAllChargerFlowsStore } from '@features/chargerFlows/store';

import PrimaryButton from '@components/PrimaryButton';

import s from './FlowsView.module.css';
import { useCreateChargerFlow } from '@features/chargerFlows/mutations';

type FlowsListProps = {
  flows: ChargerFlow[];
  isMobile: boolean;
};

const FlowsList = ({ flows, isMobile }: FlowsListProps) => {
  return (
    <div className={s.flowsViewListTable}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className={s.flowsViewTableHeader}>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              {!isMobile && <TableCell>Created at</TableCell>}
              {!isMobile && <TableCell>Last updated at</TableCell>}
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flows.map(({ id, name, type, createdAt, updatedAt }) => (
              <TableRow key={id}>
                <TableCell>{name}</TableCell>
                <TableCell>{type}</TableCell>
                {!isMobile && <TableCell>{formatIsoToDate(createdAt).toUpperCase()}</TableCell>}
                {!isMobile && <TableCell>{formatIsoToDate(updatedAt).toUpperCase()}</TableCell>}
                <TableCell>
                  <NavLink
                    to={`/dashboard/charger-flows/${id}/edit`}
                    className={s.flowsViewEditButton}>
                    <EditIcon />
                  </NavLink>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

function FlowsView() {
  const [searchResults, setSearchResults] = useState<ChargerFlow[]>([]);
  const [allFlows, setAllFlows] = useState<ChargerFlow[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { data, isLoading } = useAllChargerFlows(currentPage);
  const searchRef = useRef<HTMLInputElement>(null);
  const isMobile = useMediaQuery({ query: `(max-width: ${Breakpoints.md})` });
  const setAllChargerFlowsStore = useSetAllChargerFlowsStore();
  const { mutate: createChargerFlow } = useCreateChargerFlow();

  // Set allFlows state when data is fetched
  useEffect(() => {
    if (data && data.data && data.data.length > 0) {
      setAllFlows((prevFlows) => {
        const newFlows = data.data.filter(
          (newFlow) => !prevFlows.some((prevFlow) => prevFlow.id === newFlow.id),
        );
        return [...prevFlows, ...newFlows];
      });
    }

    return () => {
      setAllFlows([]);
    };
  }, [data]);

  // Set UI state when allFlows state is updated
  useEffect(() => {
    if (searchRef.current?.value) {
      setSearchResults(filteredResults);
      return;
    }

    setSearchResults(allFlows);
    setAllChargerFlowsStore(allFlows);
  }, [allFlows]);

  const filteredResults = useMemo(() => {
    const query = searchRef.current?.value?.toLowerCase() || '';
    return allFlows.filter((flow) => flow.name.toLowerCase().includes(query));
  }, [allFlows, searchRef.current?.value]);

  const handleSearchChange = useCallback(
    debounce((value: string) => {
      const query = value.toLowerCase();
      const filteredResults = allFlows.filter((flow) => flow.name.toLowerCase().includes(query));
      setSearchResults(filteredResults);
    }, 300),
    [allFlows],
  );

  const handleLoadMore = () => {
    if (!data || !data.meta.hasNextPage) return;

    setCurrentPage((prev) => prev + 1);
  };

  const handleCreateNew = () => {
    // Logic to create a new flow goes here
    createChargerFlow({
      chargerFlow: { name: 'New Flow', version: 'v1', content: { nodes: [], edges: [] } },
    });
  };

  return (
    <div className={s.flowsViewContainer}>
      <div className={s.flowsViewHeader}>
        <h2 className={s.flowsViewTitle}>Flows:</h2>
        <div className={s.flowsViewSearchBox}>
          <TextField
            inputRef={searchRef}
            size="small"
            label="Search by flow name"
            variant="outlined"
            className={s.flowsViewSearchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
      </div>
      <FlowsList flows={searchResults} isMobile={isMobile} />
      <div className={s.flowsViewActions}>
        <PrimaryButton
          title={'Load More'}
          isDisabled={isLoading || !data?.meta.hasNextPage || allFlows.length === 0}
          customClass={s.flowsViewsLoadMoreButton}
          icon={<DownloadingIcon />}
          onClick={handleLoadMore}
        />
        <PrimaryButton
          title={'Create New'}
          customClass={s.flowsViewsLoadMoreButton}
          icon={<NoteAddIcon fontSize="small" />}
          onClick={handleCreateNew}
        />
      </div>
    </div>
  );
}

export default FlowsView;
